'use client';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { varAlpha } from 'minimal-shared/utils';
import { Iconify } from 'src/components/iconify';
import { Logo } from 'src/components/logo';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
import { DEMO, KAPITEL } from '../landning-innehåll';
import { SPELA_FILM } from './Hjälte';

/** Finns filen? Avgörs innan något visas, så att scenen aldrig lovar en film som inte laddar. */
type Tillgång = 'kontrollerar' | 'finns' | 'saknas';

function tid(sekunder: number) {
    const m = Math.floor(sekunder / 60);
    const s = Math.floor(sekunder % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

async function finnsFil(url: string) {
    try {
        const svar = await fetch(url, { method: 'HEAD', cache: 'no-store' });
        return svar.ok;
    } catch {
        return false;
    }
}

export function DemoScen() {
    const filmRef = useRef<HTMLVideoElement>(null);
    const [film, setFilm] = useState<Tillgång>(DEMO.publicerad ? 'kontrollerar' : 'saknas');
    const [textning, setTextning] = useState(false);
    const [startad, setStartad] = useState(false);
    const [nu, setNu] = useState(0);

    useEffect(() => {
        if (!DEMO.publicerad) return undefined;
        let avbruten = false;
        Promise.all([finnsFil(DEMO.källa), finnsFil(DEMO.textning)]).then(([f, t]) => {
            if (avbruten) return;
            setFilm(f ? 'finns' : 'saknas');
            setTextning(t);
        });
        return () => {
            avbruten = true;
        };
    }, []);

    const spela = useCallback(() => {
        const v = filmRef.current;
        if (!v || film !== 'finns') return;
        setStartad(true);
        v.play().catch(() => undefined);
    }, [film]);

    // "Se demo" i första vyn startar filmen.
    useEffect(() => {
        const lyssna = () => spela();
        window.addEventListener(SPELA_FILM, lyssna);
        return () => window.removeEventListener(SPELA_FILM, lyssna);
    }, [spela]);

    const hoppa = (sekund: number) => {
        const v = filmRef.current;
        if (!v) return;
        v.currentTime = sekund;
        spela();
    };

    const aktivt = startad ? KAPITEL.reduce((acc, k, i) => (nu >= k.start ? i : acc), 0) : -1;
    const finns = film === 'finns';

    return (
        <Box
            component="section"
            id="demo"
            aria-labelledby="demo-rubrik"
            sx={{ bgcolor: 'background.neutral', py: { xs: 8, md: 12 }, scrollMarginTop: 64 }}
        >
            <Container maxWidth="lg">
                <Typography id="demo-rubrik" variant="h2" sx={{ fontSize: { xs: '1.875rem', md: '2.5rem' } }}>
                    {DEMO.rubrik}
                </Typography>
                <Typography sx={{ mt: 1.5, maxWidth: 640, color: 'text.secondary', fontSize: { md: '1.0625rem' } }}>
                    {DEMO.ingress}
                </Typography>

                <Box
                    sx={{
                        mt: { xs: 4, md: 5 },
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 280px' },
                        gap: 3,
                        alignItems: 'start'
                    }}
                >
                    <Card sx={{ position: 'relative', aspectRatio: { xs: '4 / 3', sm: '16 / 9' }, bgcolor: startad ? 'common.black' : 'background.paper' }}>
                        <video
                            ref={filmRef}
                            src={finns ? DEMO.källa : undefined}
                            poster={DEMO.stillbild}
                            preload="none"
                            playsInline
                            controls={startad}
                            onTimeUpdate={(e) => setNu(e.currentTarget.currentTime)}
                            onError={() => finns && setFilm('saknas')}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                visibility: startad ? 'visible' : 'hidden'
                            }}
                        >
                            {textning ? <track kind="captions" srcLang="sv" label="Svenska" src={DEMO.textning} default /> : null}
                        </video>

                        {!startad ? (
                            <Box
                                sx={(theme) => ({
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: { xs: 2, md: 3 },
                                    p: 3,
                                    textAlign: 'center',
                                    background: `linear-gradient(160deg, ${theme.vars.palette.primary.lighter} 0%, ${theme.vars.palette.background.paper} 70%)`
                                })}
                            >
                                <Logo disabled tabIndex={-1} aria-hidden sx={{ width: { xs: 120, md: 180 }, height: { xs: 44, md: 64 } }} />
                                {film === 'saknas' ? (
                                    <>
                                        <Box>
                                            <Typography variant="subtitle1">{DEMO.saknas.rubrik}</Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                {DEMO.saknas.text}
                                            </Typography>
                                        </Box>
                                        <Button
                                            component={RouterLink}
                                            href={paths.presentation.root}
                                            variant="outlined"
                                            color="inherit"
                                            endIcon={<Iconify icon="eva:arrow-forward-fill" />}
                                        >
                                            {DEMO.saknas.länk}
                                        </Button>
                                    </>
                                ) : (
                                    <ButtonBase
                                        onClick={spela}
                                        disabled={!finns}
                                        aria-label="Spela filmen"
                                        sx={(theme) => ({
                                            width: { xs: 64, md: 80 },
                                            height: { xs: 64, md: 80 },
                                            borderRadius: '50%',
                                            color: 'primary.contrastText',
                                            bgcolor: 'primary.main',
                                            boxShadow: theme.vars.customShadows.primary,
                                            transition: theme.transitions.create(['transform', 'background-color']),
                                            '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.04)' }
                                        })}
                                    >
                                        <Iconify icon="solar:play-circle-bold" width={40} />
                                    </ButtonBase>
                                )}
                            </Box>
                        ) : null}
                    </Card>

                    <Box component="nav" aria-label="Filmens kapitel">
                        <Typography variant="overline" sx={{ color: 'text.disabled', display: 'block', mb: 1, px: 1.5 }}>
                            Kapitel
                        </Typography>
                        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                            {KAPITEL.map((k, i) => (
                                <li key={k.titel}>
                                    <ButtonBase
                                        onClick={() => hoppa(k.start)}
                                        disabled={!finns}
                                        aria-current={i === aktivt ? 'step' : undefined}
                                        sx={(theme) => ({
                                            width: '100%',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            px: 1.5,
                                            py: 1.25,
                                            borderRadius: 1,
                                            typography: 'body2',
                                            textAlign: 'left',
                                            color: i === aktivt ? 'primary.main' : 'text.primary',
                                            fontWeight: i === aktivt ? 600 : 400,
                                            bgcolor: i === aktivt ? varAlpha(theme.vars.palette.primary.mainChannel, 0.08) : 'transparent',
                                            '&:hover': { bgcolor: 'action.hover' },
                                            '&.Mui-disabled': { color: 'text.secondary' }
                                        })}
                                    >
                                        <span>{k.titel}</span>
                                        {finns ? (
                                            <Box component="span" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                                                {tid(k.start)}
                                            </Box>
                                        ) : null}
                                    </ButtonBase>
                                </li>
                            ))}
                        </Box>
                        {finns ? (
                            <Typography variant="caption" sx={{ display: 'block', mt: 2, px: 1.5, color: 'text.disabled' }}>
                                {DEMO.märkning}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}

